import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Container, FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import styles from "./clientsHeaderBarStyles.js";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import { TabList } from "@mui/lab";
import ClientsDetailsCreditsTable from "../ClientsDetailsCreditsTable/index.js";
import ClientsDetailsBalanceTable from "../ClientsDetailsBalanceTable/index.js";
import SearchBar from "../../SharedComponents/SearchBar/index.js";
import useLogout from '../../../hooks/auth/authHook.js';
import { red } from "@mui/material/colors";
import { notification } from "../../../utils/Notifications.js";
import { updateClientCategory, updateClientAccountStatus } from "../../../api/userApi.js";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

const ClientsTabBar = ({ user, categories = [], credits, balance, isLoading, currentTap, setCurrentTap, fetchClientInfo }) => {
  const [logout] = useLogout();
  const { hasPermission, hasAllPermissions } = usePermissions();

  const [isFormActive, setIsFormActive] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [membership, setMembership] = useState(user?.membership ?? '');
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [accountStatus, setAccountStatus] = useState(user?.account_status ?? '');
  const [creditsSearch, setCreditsSearch] = useState("");
  const [balanceSearch, setBalanceSearch] = useState("");

  const handleChange = (event, newValue) => {
    setCreditsSearch("");
    setBalanceSearch("");
    setCurrentTap(newValue);
  };

  const handleUpdateButtonClick = () => {
    setIsFormActive(!isFormActive);
  }
  
  const handleCancelButtonClick = () => {
    setIsFormActive(false);
  }
  
  useEffect(() => {
    setMembership(user?.membership);
    setAccountStatus(user?.account_status);
  }, [user, categories]);

  const handleMembershipChange = (event, newValue) => {
    setMembership(event.target.value);
  };

  const handleAccountStatusChange = (event) => {
    setAccountStatus(event.target.value);
  };

  useEffect(() => {
    const firstAvailableTab =
    (hasAllPermissions(["cli_vwc", "cli_vw"]) && "1") ||
    (hasAllPermissions(["cli_vwb", "cli_vw"]) && "2") ||
    (hasAllPermissions(["cli_vwi", "cli_vw"]) && "3") ||
      null;

    if (firstAvailableTab && currentTap !== firstAvailableTab) {
      setCurrentTap(firstAvailableTab);
    }
  }, []);

  const handleCreateUpdateClientClick = () => {
    if (isLoading) {
      return;
    }
    setIsUpdateLoading(true);

    const updatePromises = [];

    if (selectedMembership) {
      updatePromises.push(updateClientCategory(user.id_user, selectedMembership));
    }
    if (accountStatus !== user?.account_status) {
      updatePromises.push(updateClientAccountStatus(user.id_user, accountStatus));
    }

    if (updatePromises.length === 0) {
      setIsUpdateLoading(false);
      setIsFormActive(false);
      return;
    }

    Promise.all(updatePromises)
      .then((responses) => {
        let hasError = false;
        let isUnauthorized = false;
        
        responses.forEach((response) => {
          if (response.data.status_Code === 401) {
            isUnauthorized = true;
          } else if (response.data.status_Code !== 200) {
            hasError = true;
          }
        });

        if (isUnauthorized) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else if (hasError) {
          notification({
            text: "Error al cambiar la información",
            type: "error",
          });
        } else {
          notification({ text: "Información actualizada", type: "success" });
          fetchClientInfo();
        }
      })
      .catch(() => {
        setIsUpdateLoading(false);
        setIsFormActive(false);
        notification({text: 'Error de conexión, revisa tu conexión a internet', type: 'error'});
      })
      .finally(() => {
        setIsFormActive(false);
        setIsUpdateLoading(false);
        setSelectedMembership(null);
      });
  };

  return (
    <Container style={styles.primaryContainer}>
      <TabContext value={currentTap}>
        <Container style={styles.mainContainer}>
          <Box sx={styles.tabContainer}>
            <TabList
              onChange={handleChange}
              aria-label="Clients tabs"
              variant="fullWidth"
            >
              {hasAllPermissions(["cli_vwi", "cli_vw", "cli_ed_sett"]) && (
                <Tab label="Información" value="3" />
              )}

              {hasAllPermissions(["cli_vwc", "cli_vw"]) && (
                <Tab label="Créditos" value="1" />
              )}

              {hasAllPermissions(["cli_vwb", "cli_vw"]) && (
                <Tab label="Saldos" value="2" />
              )}
            </TabList>
          </Box>
        </Container>

        <Box sx={{ width: "100%", padding: '0px !important'}}>
          <TabPanel value="3" sx={{ padding: '0px !important'}}>
            <Container style={styles.mainContainer} >
            {hasAllPermissions(["cli_ed_sett", 'cli_vw', 'cli_vwi']) && (<Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">Categoría</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    value={membership}
                    label="Categoría"
                    disabled={!isFormActive}
                    onChange={handleMembershipChange}
                  >
                    {
                      categories?.map((category, index) => {
                        return <MenuItem onClick={() => { setSelectedMembership(category.id_membership_type); }} key={category?.id_membership_type} value={category?.membership_name}>{category?.membership_name}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormControl fullWidth>
                  <InputLabel id="account-status-select-label">Estatus de la cuenta</InputLabel>
                  <Select
                    labelId="account-status-select-label"
                    value={accountStatus || ''}
                    label="Estatus de la cuenta"
                    disabled={!isFormActive}
                    onChange={handleAccountStatusChange}
                  >
                    <MenuItem value="Verificada">Verificada</MenuItem>
                    <MenuItem value="Sin verificar">Sin verificar</MenuItem>
                    <MenuItem value="Suspendida">Suspendida</MenuItem>
                    <MenuItem value="Bloqueada">Bloqueada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>)}
            <Container sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 1, padding: '0px !important', marginTop: 3 }}>
              { isFormActive && <Button
                variant="standard"
                color="primary"
                onClick={handleCancelButtonClick}
                style={{
                  width: "120px",
                  borderRadius: "0.5rem",
                  height: "38px",
                  color: red["A400"],
                  backgroundColor: red["50"],
                }}
              >
                Cancelar
              </Button>}
              {hasAllPermissions(["cli_ed_sett", "cli_ed_d", "cli_vw", "cli_vwi"]) && (<Button
                variant="contained"
                disabled={false}
                onClick={isFormActive ? handleCreateUpdateClientClick : handleUpdateButtonClick}
                sx={true ? styles.activeButton : styles.inactiveButton}
              >
                {isUpdateLoading ? (
                  <CircularProgress size={24} style={styles.circularProgress} />
                ) : (
                  isFormActive ? "Guardar" : "Editar"
                )}
              </Button>)}
            </Container>
            </Container>
          </TabPanel>

          <TabPanel value="1" sx={{ padding: '0px !important'}}>
            { !isLoading && <SearchBar setSearch={setCreditsSearch} />}
            <Container
              style={styles.mainContainer}
            >
              <ClientsDetailsCreditsTable isLoading={isLoading} creditsHistory={credits?.credits_history} creditsSearch={creditsSearch} />
            </Container>
          </TabPanel>

          <TabPanel value="2" sx={{ padding: '0px !important'}}>
            { !isLoading && <SearchBar setSearch={setBalanceSearch} />}
            <Container style={styles.mainContainer} >
              <ClientsDetailsBalanceTable isLoading={isLoading} balanceHistory={balance?.balance_history} balanceSearch={balanceSearch} />
            </Container>
          </TabPanel>
        </Box>
      </TabContext>
    </Container>
  );
};

ClientsTabBar.propTypes = {
  currentTap: PropTypes.string,
  handleChange: PropTypes.func,
};

export default ClientsTabBar;
